/**
 * exportShiftHtml — builds the FOM Inventory Log as self-contained HTML.
 * Reads from localStorage. Returns complete <html> string for print/Word export.
 *
 * Changes from original:
 *  - Font size 7pt → 9pt (more legible)
 *  - Removed "(Sup: xx:xx-xx:xx)" from shift line
 *  - Shift computed from current ET time (not just localStorage)
 *  - Other Radios: radio number (105/108/113/118) in first column, callsign label second
 *  - All keys default to ✔ unless name assigned
 *  - Access badges filled in with proper status (✔ / name / OOS)
 *  - Recorders layout matches original template exactly
 */

interface PersonRow {
  role: string
  name: string
  key: string
  radio: string
  recorder: string
  badge: string
  callSign: string
  shiftFrom: string
  shiftTo: string
  posts: { post: string; isBreak: boolean }[]
}

interface SecKey      { number: number; assignedTo: string; status: string }
interface RadioRow    { id: string; assignedTo: string; available?: boolean }
interface RecorderRow { id: string; assignedTo: string; oos?: boolean; available?: boolean }
interface BadgeRow    { id: number; status: string }
interface FlashRow    { id: number; assignedTo: string; available: boolean }
interface AccessBadge { id: number; available: boolean; oos?: boolean; assignedTo?: string }

function computeShift(): string {
  try {
    const h = parseInt(
      new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', hour12: false }).split(':')[0],
    )
    return h >= 7 && h < 15 ? 'Morning' : h >= 15 && h < 23 ? 'Evening' : 'Night'
  } catch {
    const h = new Date().getHours()
    return h >= 7 && h < 15 ? 'Morning' : h >= 15 && h < 23 ? 'Evening' : 'Night'
  }
}

export function generateShiftHtml(forWord: boolean = true): string {
  try {
    const date          = localStorage.getItem('fom_currentDate') ?? ''
    const shift         = computeShift()
    const weather       = JSON.parse(localStorage.getItem('fom_weather') ?? '{}')
    const pers          = JSON.parse(localStorage.getItem('fom_personnel') ?? '[]') as PersonRow[]
    const keys          = JSON.parse(localStorage.getItem('fom_securityKeys') ?? '[]') as SecKey[]
    const supRadios     = JSON.parse(localStorage.getItem('fom_supervisorRadios') ?? '[]') as RadioRow[]
    const offRadios     = JSON.parse(localStorage.getItem('fom_officerRadios') ?? '[]') as RadioRow[]
    const otherRad      = JSON.parse(localStorage.getItem('fom_otherRadios') ?? '[]') as RadioRow[]
    const recorders     = JSON.parse(localStorage.getItem('fom_recorders') ?? '[]') as RecorderRow[]
    const badges        = JSON.parse(localStorage.getItem('fom_badges') ?? '[]') as BadgeRow[]
    const deskOff       = localStorage.getItem('fom_deskOfficer') ?? ''
    const oosIds        = JSON.parse(localStorage.getItem('fom_oosIds') ?? '[]') as number[]
    const flashlights   = JSON.parse(localStorage.getItem('fom_flashlights') ?? '[]') as FlashRow[]
    const accessBadges  = JSON.parse(localStorage.getItem('fom_accessBadges') ?? '[]') as AccessBadge[]
    const otherKeys     = JSON.parse(localStorage.getItem('fom_otherKeys') ?? '[]') as Array<{ number: number; assignedTo?: string; available?: boolean; oos?: boolean }>

    // Authoritative OOS register — the single source of truth across devices.
    // Every category's OOS state lives here (keys, officerRadios, recorders,
    // badges, etc.). The export must consult this so ALL out-of-service items
    // show OOS, not just the ones whose per-array flag happened to be set.
    const oosState = (() => {
      try { return JSON.parse(localStorage.getItem('fom_oos_state') ?? '{}') } catch { return {} }
    })() as Record<string, (string | number)[]>
    const oosHas = (cat: string, id: string | number) =>
      Array.isArray(oosState[cat]) && oosState[cat].map(String).includes(String(id))
    // Officer badges OOS = union of the legacy oosIds array and the register.
    const badgeOos = (id: number) => oosIds.includes(id) || oosHas('badges', id)

    // Last name only
    const Re = (n: string) => {
      const s = n
        .replace(/\s*\(.*?\)\s*/g, '')
        .replace(/^(Dir\.?|AD\.?|Lt\.?|Sgt\.?|Ofc\.?)\s*/i, '')
        .trim()
      return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
    }

    const shiftRange = (s: string) =>
      s === 'Morning' ? '07:00 - 15:00' : s === 'Evening' ? '15:00 - 23:00' : '23:00 - 07:00'

    // ── Status helpers ──────────────────────────────────────────────────────

    // Officer Badges: show ONLY officers (role 'Officer'). Supervisors and FCPD
    // never appear in the badge grid. OOS pulled from the authoritative register.
    const badgeStatus = (id: number) => {
      if (badgeOos(id)) return 'OOS'
      const p = pers.find(x => x.badge === String(id) && x.role === 'Officer' && x.name)
      if (p) return Re(p.name)
      return '✔'
    }

    const keyStatus = (num: number) => {
      if (oosHas('keys', num)) return 'OOS'
      const p = pers.find(x => x.key === String(num))
      if (p && p.name) return Re(p.name)
      // Security keys (1–7) live in fom_securityKeys; all the other numbered
      // keys (contractors, mall dept) live in fom_otherKeys. Check BOTH so an
      // "other key" that's checked out shows the holder's name, not ✔. The
      // name is whoever was entered (officer / management / contractor).
      const k = keys.find(x => x.number === num)
      if (k) {
        if ((k as any).oos) return 'OOS'
        if (k.assignedTo) return Re(k.assignedTo)
      }
      const ok = otherKeys.find(x => x.number === num)
      if (ok) {
        if (ok.oos) return 'OOS'
        if (ok.assignedTo) return Re(ok.assignedTo)
      }
      return '✔'
    }

    const supRadio = (id: string) => {
      if (oosHas('supervisorRadios', id)) return 'OOS'
      const p = pers.find(x => x.radio === id)
      if (p && p.name) return Re(p.name)
      const r = supRadios.find(x => x.id === id)
      if (!r) return '✔'
      return r.assignedTo ? Re(r.assignedTo) : '✔'
    }

    const offRadio = (id: string) => {
      if (oosHas('officerRadios', id)) return 'OOS'
      const p = pers.find(x => x.radio === id)
      if (p && p.name) return Re(p.name)
      const r = offRadios.find(x => x.id === id)
      if (!r) return '✔'
      return r.assignedTo ? Re(r.assignedTo) : '✔'
    }

    const otherRadio = (id: string) => {
      if (oosHas('otherRadios', id)) return 'OOS'
      const p = pers.find(x => x.radio === id)
      if (p && p.name) return Re(p.name)
      const r = otherRad.find(x => x.id === id)
      if (!r) return '✔'
      // Spare radios: show whoever was entered (any person type) when checked
      // out. assignedTo non-empty wins regardless of the available flag.
      if (r.assignedTo) return Re(r.assignedTo)
      return '✔'
    }

    const recStatus = (id: number) => {
      if (oosHas('recorders', id)) return 'OOS'
      const r = recorders.find(x => x.id === String(id))
      if (!r) return '✔'
      if (r.oos) return 'OOS'
      const p = pers.find(x => x.recorder === String(id))
      if (p && p.name) return Re(p.name)
      return r.assignedTo ? Re(r.assignedTo) : '✔'
    }

    const flashStatus = (id: number) => {
      if (oosHas('flashlights', id)) return 'OOS'
      const f = flashlights.find(x => x.id === id)
      if (!f) return '✔'
      return f.assignedTo ? Re(f.assignedTo) : '✔'
    }

    const abStatus = (id: number) => {
      if (oosHas('accessBadges', id)) return 'OOS'
      // Access badge ids are stored as STRINGS ("1","2"...) in fom_accessBadges,
      // but this helper is called with NUMBERs. Compare as strings so the
      // lookup matches — x.id === id was "2" === 2 (always false), which made
      // every badge fall through to ✔ even when a name was assigned.
      const b = accessBadges.find(x => String(x.id) === String(id))
      if (!b) return '✔'
      if (b.oos) return 'OOS'
      // Show the entered name whenever one is assigned — don't depend solely on
      // the available flag (it isn't always flipped when a name is entered).
      if (b.assignedTo) return Re(b.assignedTo)
      return '✔'
    }

    const postAbbr = (p: PersonRow) => {
      if (!p.posts?.length) return ''
      const set = new Set(
        p.posts
          .filter(x => x.post !== 'Break' && !x.isBreak)
          .map(x => {
            const m: Record<string, string> = {
              Mall: 'M', Vehicle: 'V', Desk: 'D',
              Special: 'SP', Escalators: 'ESC', Detex: 'DX',
            }
            return m[x.post] || x.post.charAt(0)
          })
          .filter(Boolean),
      )
      return Array.from(set).join(', ')
    }

    const shiftTime = (p: PersonRow) =>
      p.shiftFrom || p.shiftTo ? `${p.shiftFrom || ''}-${p.shiftTo || ''}` : ''

    // ── Personnel grid ──────────────────────────────────────────────────────
    const dirSlots = pers.filter(p => p.role === 'Dir/AD/Lt')
    const sgtSlots = pers.filter(p => p.role === 'Sergeant')
    const oicSlot  = pers.find(p => p.role === 'OIC') ?? {
      role: 'OIC', name: '', key: '', radio: '', recorder: '',
      callSign: '', shiftFrom: '', shiftTo: '', posts: [], badge: '',
    }
    const officers  = pers.filter(p => p.role === 'Officer')
    const dirName   = dirSlots.map(p => p.name).filter(Boolean).map(Re).join(' / ')
    const sgtName   = sgtSlots.map(p => p.name).filter(Boolean).map(Re).join(' / ')
    const multSgt   = sgtSlots.filter(p => p.name).length > 1
    // Supervisor line: supervisors only. Exclude Officers AND FCPD — FCPD are
    // never listed as personnel; they appear solely via their radio assignment
    // (108/118 = SC-82/SC-81 in Other Radios).
    const supervisors = pers.filter(p => p.role !== 'Officer' && p.role !== 'FCPD' && p.name).map(p => Re(p.name))

    type GridRow = PersonRow & { _dir?: boolean; _blank?: boolean }
    const grid: GridRow[] = [
      {
        ...(dirSlots[0] ?? { role: 'Dir/AD/Lt', name: '', key: '', radio: '', recorder: '', callSign: '', shiftFrom: '', shiftTo: '', posts: [], badge: '' }),
        name: dirName, _dir: true,
      },
      {
        ...(sgtSlots[0] ?? { role: 'Sergeant', name: '', key: '', radio: '', recorder: '', callSign: '', shiftFrom: '', shiftTo: '', posts: [], badge: '' }),
        name: sgtName, _blank: multSgt,
      },
      oicSlot,
      ...officers.slice(0, 6),
    ]
    while (grid.length < 9) {
      grid.push({ role: 'Officer', name: '', key: '', radio: '', recorder: '', callSign: '', shiftFrom: '', shiftTo: '', posts: [], badge: '', _dir: false, _blank: false })
    }

    const roleLabels  = ['Dir/AD/Lt', 'Supervisor', 'OIC', 'Officer', 'Officer', 'Officer', 'Officer', 'Officer', 'Officer']
    const assignCodes = ['', 'D = Desk', 'M = Mall', 'V = Vehicle', 'SP = Special', '', '', '', '']

    const pRows = grid.map((p, i) => {
      const blank = p._dir || p._blank
      return `<tr>
        <td colspan="2" style="font-style:italic">${roleLabels[i] ?? ''}</td>
        <td colspan="3">${p.name ? Re(p.name) : ''}</td>
        <td colspan="2">${blank ? '' : shiftTime(p)}</td>
        <td>${blank ? '' : (p.key || '')}</td>
        <td colspan="2">${blank ? '' : (p.radio || '')}</td>
        <td colspan="2">${blank ? '' : (p.recorder || '')}</td>
        <td colspan="4">${blank ? '' : postAbbr(p)}</td>
        <td colspan="4">${assignCodes[i] ?? ''}</td>
      </tr>`
    }).join('\n')

    // ── HTML output ─────────────────────────────────────────────────────────
    return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head><meta charset="utf-8">
${forWord ? `<!-- Word-specific page margins. MS Word ignores the CSS @page rule below and
     uses its own page setup, so margins MUST be declared here via a named
     WordSection with mso margins. This block is ONLY emitted for the Word
     export download — NOT for the browser print window, where the <xml>/<w:>
     namespace tag breaks HTML parsing and the table fails to render (the
     'header/footer but no table' bug). -->
<xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml>` : ''}
<style>
@page WordSection1 { size: 8.5in 11in; margin: 0.1in 0.2in 0.1in 0.2in; mso-page-orientation: portrait; }
div.WordSection1 { page: WordSection1; }
@page { size: letter portrait; margin: 0.1in 0.2in; }
@media print { @page { size: letter portrait; margin: 0.1in 0.2in; } body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
html { min-height: 100%; }
body { font-family: Calibri, Arial, sans-serif; font-size: 9pt; margin: 0; padding: 0; }
h1 { font-size: 13pt; text-align: center; margin: 0 0 2px; font-weight: normal; }
h1 span { font-size: 17pt; }
table { border-collapse: collapse; width: 100%; table-layout: fixed; }
td, th { border: 1px solid #000; padding: 1px 3px; font-size: 9pt; vertical-align: middle; line-height: 1.15; text-align: center; overflow: hidden; }
.nb { border: none; }
.c { text-align: center; }
.b { font-weight: bold; }
.i { font-style: italic; }
.bg { background-color: #d9d9d9; }
.section-hdr { text-align: center; font-weight: bold; font-size: 9pt; background-color: #d9d9d9; padding: 0; }
.sub-hdr { text-align: center; font-size: 9pt; font-weight: bold; background-color: #d9d9d9; }
</style>
</head><body>
<div class="WordSection1">
<h1><span>F</span>air Oaks Mall Security Department Inventory Log</h1>
<table>
<!-- HEADER ROWS -->
<tr>
  <td colspan="2" class="i">Date</td>
  <td colspan="3">${date}</td>
  <td colspan="3" class="i">Desk Officer</td>
  <td colspan="4">${Re(deskOff)}</td>
  <td colspan="2" class="i">Weather</td>
  <td colspan="6">${weather.conditions ?? ''}</td>
</tr>
<tr>
  <td colspan="2" class="i">Shift</td>
  <td colspan="3">${shiftRange(shift)}</td>
  <td colspan="3" class="i">Supervisor</td>
  <td colspan="4">${supervisors.join(' / ') || ''}</td>
  <td colspan="2" class="i">Temp</td>
  <td colspan="6">${weather.temp ?? ''}</td>
</tr>
<!-- PERSONNEL COLUMN HEADERS -->
<tr class="bg">
  <td colspan="2"></td>
  <td colspan="3" class="c b">Name</td>
  <td colspan="2" class="c b">Time</td>
  <td class="c b">Key</td>
  <td colspan="2" class="c b">Radio</td>
  <td colspan="2" class="c b">Recorder</td>
  <td colspan="4" class="c b">Assignment</td>
  <td colspan="4" class="c b">Assignment Codes</td>
</tr>
<!-- PERSONNEL ROWS -->
${pRows}
<!-- EQUIPMENT CHECKLIST -->
<tr>
  <td colspan="20" class="section-hdr" style="padding:2px 0;">
    <strong>Equipment Checklist</strong><br/>
    <span style="font-weight:normal;font-size:8pt;">(mark a ✔ if item is in drawer, or name of person if checked out)</span>
  </td>
</tr>
<tr class="bg">
  <td class="b">Key #</td>
  <td colspan="3" class="b">Description</td>
  <td colspan="3">Name</td>
  <td colspan="13" class="b c">Inventory</td>
</tr>
<!-- SECURITY + Officer Badges -->
<tr class="bg">
  <td colspan="7" class="b">SECURITY</td>
  <td colspan="13" class="sub-hdr">Officer Badges</td>
</tr>
<tr><td>1</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(1)}</td><td class="c">1</td><td colspan="2">${badgeStatus(1)}</td><td class="c">5</td><td colspan="2">${badgeStatus(5)}</td><td class="c">9</td><td colspan="2">${badgeStatus(9)}</td><td class="c">13</td><td colspan="3">${badgeStatus(13)}</td></tr>
<tr><td>2</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(2)}</td><td class="c">2</td><td colspan="2">${badgeStatus(2)}</td><td class="c">6</td><td colspan="2">${badgeStatus(6)}</td><td class="c">10</td><td colspan="2">${badgeStatus(10)}</td><td class="c">14</td><td colspan="3">${badgeStatus(14)}</td></tr>
<tr><td>3</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(3)}</td><td class="c">3</td><td colspan="2">${badgeStatus(3)}</td><td class="c">7</td><td colspan="2">${badgeStatus(7)}</td><td class="c">11</td><td colspan="2">${badgeStatus(11)}</td><td class="c">15</td><td colspan="3">N/A</td></tr>
<tr><td>4</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(4)}</td><td class="c">4</td><td colspan="2">${badgeStatus(4)}</td><td class="c">8</td><td colspan="2">${badgeStatus(8)}</td><td class="c">12</td><td colspan="2">${badgeStatus(12)}</td><td class="c">16</td><td colspan="3">N/A</td></tr>
<!-- Flashlights -->
<tr><td>5</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(5)}</td><td colspan="13" class="sub-hdr">Flashlights</td></tr>
<tr><td>6</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(6)}</td><td class="c">1</td><td colspan="3">Vehicle</td><td class="c">2</td><td colspan="3">${flashStatus(2)}</td><td class="c">3</td><td colspan="4">${flashStatus(3)}</td></tr>
<!-- Supervisor Radios -->
<tr><td>7</td><td colspan="3" class="c">Security</td><td colspan="3">${keyStatus(7)}</td><td colspan="13" class="sub-hdr">Supervisor's Radios</td></tr>
<tr><td>8</td><td colspan="3" class="c">Sec Veh 1 (spare)</td><td colspan="3">${keyStatus(8)}</td><td colspan="3">SGT-100</td><td colspan="4">${supRadio('SGT-100')}</td><td colspan="3">SGT-101</td><td colspan="3">${supRadio('SGT-101')}</td></tr>
<!-- Mall Dept + Officer Radios -->
<tr class="bg"><td colspan="7" class="b">Mall Department</td><td colspan="13" class="sub-hdr">Officer's Radios</td></tr>
<tr><td>9</td><td colspan="3" class="c">Generator</td><td colspan="3">${keyStatus(9)}</td><td colspan="3">102</td><td colspan="4">${offRadio('102')}</td><td colspan="3">115</td><td colspan="3">${offRadio('115')}</td></tr>
<tr><td>10</td><td colspan="3" class="c">Traffic Light Box</td><td colspan="3">${keyStatus(10)}</td><td colspan="3">103</td><td colspan="4">${offRadio('103')}</td><td colspan="3">109</td><td colspan="3">${offRadio('109')}</td></tr>
<tr><td>11</td><td colspan="3" class="c">New Wing SMK detect</td><td colspan="3">${keyStatus(11)}</td><td colspan="3">104</td><td colspan="4">${offRadio('104')}</td><td colspan="3">110</td><td colspan="3">${offRadio('110')}</td></tr>
<tr><td>12</td><td colspan="3" class="c">Sink Valve</td><td colspan="3">${keyStatus(12)}</td><td colspan="3">114</td><td colspan="4">${offRadio('114')}</td><td colspan="3">111</td><td colspan="3">${offRadio('111')}</td></tr>
<tr><td>13</td><td colspan="3" class="c">Water Valve</td><td colspan="3">${keyStatus(13)}</td><td colspan="3">106</td><td colspan="4">${offRadio('106')}</td><td colspan="3">112</td><td colspan="3">${offRadio('112')}</td></tr>
<tr><td>14</td><td colspan="3" class="c">Elevator Key</td><td colspan="3">${keyStatus(14)}</td><td colspan="3">107</td><td colspan="4">${offRadio('107')}</td><td colspan="3">117</td><td colspan="3">${offRadio('117')}</td></tr>
<!-- Other Radios — radio number first column, callsign label second -->
<tr><td>15</td><td colspan="3" class="c">J Valve Key</td><td colspan="3">${keyStatus(15)}</td><td colspan="13" class="sub-hdr">Other Radios</td></tr>
<tr><td>17</td><td colspan="3" class="c">H &amp; M Key</td><td colspan="3">${keyStatus(17)}</td><td colspan="2">105</td><td colspan="4">E-3</td><td colspan="2">28</td><td colspan="5">${otherRadio('28')}</td></tr>
<tr><td>19</td><td colspan="3" class="c">Master Pad Lock</td><td colspan="3">${keyStatus(19)}</td><td colspan="2">108</td><td colspan="4">SC-82</td><td colspan="2">50</td><td colspan="5">${otherRadio('50')}</td></tr>
<tr><td>27</td><td colspan="3" class="c">Electric Disc Tenant</td><td colspan="3">${keyStatus(27)}</td><td colspan="2">113</td><td colspan="4">E-2</td><td colspan="2">51</td><td colspan="5">${otherRadio('51')}</td></tr>
<tr><td>28</td><td colspan="3" class="c">J Parking Elevator</td><td colspan="3">${keyStatus(28)}</td><td colspan="2">118</td><td colspan="4">SC-81</td><td colspan="2">52</td><td colspan="5">${otherRadio('52')}</td></tr>
<tr><td>30</td><td colspan="3" class="c">Emer Elevator Key</td><td colspan="3">${keyStatus(30)}</td><td colspan="2">10</td><td colspan="4">${otherRadio('10')}</td><td colspan="2">R-23</td><td colspan="5">${otherRadio('R-23')}</td></tr>
<!-- Digital Recorders -->
<tr><td>35</td><td colspan="3" class="c">Emer Emply Bath Bypass</td><td colspan="3">${keyStatus(35)}</td><td colspan="13" class="sub-hdr">Digital Recorders</td></tr>
<tr class="bg"><td colspan="7" class="b">Contractors</td><td colspan="2" class="c">1</td><td colspan="4">${recStatus(1)}</td><td colspan="2" class="c">7</td><td colspan="5">${recStatus(7)}</td></tr>
<tr><td>16</td><td colspan="3" class="c">Castle Sprinkler</td><td colspan="3">${keyStatus(16)}</td><td colspan="2" class="c">2</td><td colspan="4">${recStatus(2)}</td><td colspan="2" class="c">8</td><td colspan="5">${recStatus(8)}</td></tr>
<tr><td>18</td><td colspan="3" class="c">Specified Electrical</td><td colspan="3">${keyStatus(18)}</td><td colspan="2" class="c">3</td><td colspan="4">${recStatus(3)}</td><td colspan="2" class="c">9</td><td colspan="5">${recStatus(9)}</td></tr>
<tr><td>20</td><td colspan="3" class="c">Reliable</td><td colspan="3">${keyStatus(20)}</td><td colspan="2" class="c">4</td><td colspan="4">${recStatus(4)}</td><td colspan="2" class="c">10</td><td colspan="5">${recStatus(10)}</td></tr>
<tr><td>21</td><td colspan="3" class="c">MACC</td><td colspan="3">${keyStatus(21)}</td><td colspan="2" class="c">5</td><td colspan="4">${recStatus(5)}</td><td colspan="2" class="c">11</td><td colspan="5">${recStatus(11)}</td></tr>
<tr><td>22</td><td colspan="3" class="c">KONE</td><td colspan="3">${keyStatus(22)}</td><td colspan="2" class="c">6</td><td colspan="4">${recStatus(6)}</td><td colspan="7"></td></tr>
<!-- Access Badges -->
<tr><td>23</td><td colspan="3" class="c">Ambius</td><td colspan="3">${keyStatus(23)}</td><td colspan="13" class="sub-hdr">Access Badges</td></tr>
<tr><td>24</td><td colspan="3" class="c">Yes Energy (AMS)</td><td colspan="3">${keyStatus(24)}</td><td colspan="2" class="c">1</td><td colspan="4">${abStatus(1)}</td><td colspan="2" class="c">6</td><td colspan="5">${abStatus(6)}</td></tr>
<tr><td>25</td><td colspan="3" class="c">JS Networks</td><td colspan="3">${keyStatus(25)}</td><td colspan="2" class="c">2</td><td colspan="4">${abStatus(2)}</td><td colspan="2" class="c">7</td><td colspan="5">${abStatus(7)}</td></tr>
<tr><td>26</td><td colspan="3" class="c">Beales Plumbing</td><td colspan="3">${keyStatus(26)}</td><td colspan="2" class="c">3</td><td colspan="4">${abStatus(3)}</td><td colspan="2" class="c">8</td><td colspan="5">${abStatus(8)}</td></tr>
<tr><td>29</td><td colspan="3" class="c">JC Decaux</td><td colspan="3">${keyStatus(29)}</td><td colspan="2" class="c">4</td><td colspan="4">${abStatus(4)}</td><td colspan="2" class="c">9</td><td colspan="5">${abStatus(9)}</td></tr>
<tr><td>31</td><td colspan="3" class="c">Visual Merch Key</td><td colspan="3">${keyStatus(31)}</td><td colspan="2" class="c">5</td><td colspan="4">${abStatus(5)}</td><td colspan="2" class="c">10</td><td colspan="5">${abStatus(10)}</td></tr>
<!-- Satellite Phone -->
<tr><td>32</td><td colspan="3" class="c">Presto Pest Control</td><td colspan="3">${keyStatus(32)}</td><td colspan="13" class="sub-hdr">Satellite Phone</td></tr>
<tr><td>33</td><td colspan="3" class="c">I-Tech CCTV</td><td colspan="3">${keyStatus(33)}</td><td colspan="3">Phone 1</td><td colspan="10">✔</td></tr>
<!-- Footer -->
<tr><td>34</td><td colspan="3" class="c">AArdvark</td><td colspan="3">${keyStatus(34)}</td><td colspan="13" class="c">Last Updated ${date} – REV 1.4</td></tr>
</table>
</div>
</body></html>`
  } catch {
    return '<html><body>Error generating report</body></html>'
  }
}

/** Trigger a Word doc download of the current shift inventory log. */
export function downloadShiftDoc() {
  const html  = generateShiftHtml()
  const date  = localStorage.getItem('fom_currentDate') ?? 'unknown'
  const shift = computeShift()
  const blob  = new Blob(['\uFEFF', html], { type: 'application/msword' })
  const url   = URL.createObjectURL(blob)
  Object.assign(document.createElement('a'), {
    href: url,
    download: `Security_Inventory_${date}_${shift}.doc`,
  }).click()
  URL.revokeObjectURL(url)
}

/** Open the current shift inventory log in a new window and trigger Print.
 *  Reverted to window.open (from the iframe attempt) because the iframe +
 *  srcdoc path rendered blank for this doc — the document's height:99% /
 *  overflow:hidden + table height:calc(100%-24px) collapse inside an iframe.
 *  A real window gives those percentage heights a viewport to resolve against,
 *  which is what made the table show before. */
export function printShiftDoc() {
  const w = window.open('', '_blank')
  if (w) {
    // forWord=false: omit the Word-only <xml> block. In a browser print window
    // that tag breaks HTML parsing and the table never renders (header/footer
    // showed but the table didn't). Word export still passes forWord=true.
    w.document.write(generateShiftHtml(false))
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 400)
  } else {
    alert('Pop-up blocked. Please allow pop-ups to print.')
  }
}
